/**
 * <pond-product-addons> — selectable add-on cards on the PDP.
 *
 * Each card is a <label> wrapping a hidden <input type="checkbox">; the
 * label handles click and Space toggling natively and the checkbox holds
 * the canonical selected state. CSS drives visual state via :has(:checked)
 * so the UI works even if this JS fails to load. The size <select> sits
 * outside the label (sibling, not nested) — clicking it doesn't toggle
 * the parent checkbox, which keeps semantics clean and avoids any
 * nested-interactive ARIA violation.
 *
 * On submit, the component intercepts the form's submit in the capture
 * phase, builds an items[] payload that includes the main variant plus
 * any checked add-ons (each with a `parent_id` pointing at the main
 * variant), and POSTs to /cart/add.js. Shopify's native nested cart lines
 * group the add-ons under the parent and auto-remove them if the parent
 * is removed.
 *
 * Money formatting uses Horizon's built-in `@theme/money-formatting`
 * with the active market's `shop.money_format` (server-rendered into a
 * <template>) so kit totals match Liquid's `| money` output exactly —
 * symbol position, separators, decimals, all of it.
 *
 * Markets/multi-currency: the add-on prices in the `custom.addons` metafield
 * are the shop's BASE currency (a DKK mirror), which does NOT respect Shopify
 * Markets fixed per-market price lists. So on load we fetch each add-on's real
 * price from `/products/{handle}.js` — a same-origin request that inherits the
 * storefront's market cookie and returns the localized fixed price-list amount
 * (e.g. €30.00 on the Eurozone market, not the DKK base and not an FX-converted
 * approximation). Those market cents overwrite the base-currency data-* attrs;
 * once loaded we stop applying Shopify.currency.rate to that card. Before the
 * fetch resolves (or if it fails) we fall back to base-currency × currency.rate
 * so the price is approximate but never blank.
 */

import { formatMoney } from '@theme/money-formatting';
import { CartAddEvent, CartErrorEvent } from '@theme/events';

class PondProductAddons extends HTMLElement {
  connectedCallback() {
    this.form = this.closest('form') || document.querySelector('product-form-component form');
    if (!this.form) return;

    this.cards = this.querySelectorAll('[data-pond-addon]');
    this.details = this.querySelector('[data-pond-addons-details]');
    this.section = this.closest('section') || document;
    this.basePriceText = null; // cached text of the ATC price element with no add-ons

    // Server-rendered money_format for the active market; falls back to '{{amount}}'
    const formatTemplate = this.querySelector('template[data-pond-money-format]');
    this.moneyFormat = formatTemplate?.content?.textContent?.trim() || '{{amount}}';

    // Native checkbox-in-label pattern: the <label> handles click and Space
    // toggling natively, no JS keyboard work needed. We listen to the checkbox's
    // change event to run our side-effects (auto-pick a size, recompute total).
    for (const card of this.cards) {
      const checkbox = card.querySelector('[data-pond-addon-checkbox]');
      const sizeTrigger = card.querySelector('[data-pond-addon-size-trigger]');
      const sizeOptions = card.querySelectorAll('[data-pond-addon-size-option]');

      // Snapshot the placeholder text on the trigger so we can restore it
      // when the card deselects (without needing a runtime Liquid lookup).
      if (sizeTrigger) {
        const current = sizeTrigger.querySelector('[data-pond-addon-size-current]');
        sizeTrigger.dataset.placeholder = current?.textContent?.trim() || '';
      }

      if (checkbox) {
        checkbox.addEventListener('change', () => {
          this.#syncCardFromCheckbox(card);
          this.#applyKitTotal();
        });
      }

      // Open/close is native: the trigger's `popovertarget` toggles the popover
      // and `popover="auto"` dismisses on outside-click/Escape (and auto-closes
      // any other open size list). We only add ArrowDown/Up-to-open, and mirror
      // the popover's toggle onto aria-expanded + move focus into the list.
      const sizeList = card.querySelector('[data-pond-addon-size-list]');
      if (sizeTrigger) {
        sizeTrigger.addEventListener('keydown', (e) => this.#onSizeTriggerKeydown(card, e));
      }
      if (sizeList) {
        sizeList.addEventListener('toggle', (e) => this.#onSizeListToggle(card, e));
      }

      for (const opt of sizeOptions) {
        opt.addEventListener('click', (e) => {
          e.stopPropagation();
          if (opt.getAttribute('aria-disabled') === 'true') return;
          this.#selectSizeOption(card, opt);
          this.#applyKitTotal();
        });
        opt.addEventListener('keydown', (e) => this.#onSizeOptionKeydown(card, opt, e));
      }
    }

    // Normalize each card's server-rendered price into the active market's
    // currency on load. The Liquid `| money` fallback formats the base-currency
    // amount with the active market's format string WITHOUT converting the value,
    // so on a non-base market it shows the base amount (e.g. the DKK number) with
    // the active symbol (e.g. €) — the "DKK values labelled EUR" bug. Re-render
    // from data-default-price × Shopify.currency.rate here so the displayed price
    // is correct at load, matching the interactive #updateCardPrice path. On the
    // base market rate is 1, so this just re-formats to the identical string.
    for (const card of this.cards) {
      this.#updateCardPrice(card);
    }

    // Replace the base-currency approximation above with the real per-market
    // fixed price-list price from /products/{handle}.js (see #loadMarketPrices).
    this.#loadMarketPrices();

    // Cooperate with Horizon's product-form by overriding `handleSubmit` on the
    // <product-form-component> instance. Horizon's on:submit delegation lives at
    // document-capture (assets/component.js), so a form-level capture listener
    // can't beat it — Horizon would still POST the main item, racing ours and
    // producing a double-add. The public method is the documented extension
    // hook (blocks/buy-buttons.liquid uses on:submit="/handleSubmit"), so
    // wrapping it has a much smaller blast radius than fighting the delegate.
    // <pond-product-addons> is rendered as a sibling block of <product-form-component>
    // inside the same section (see sections/main-product-pas.liquid), so closest()
    // wouldn't find it — scope-search within the section instead. Mirrors the
    // fallback pattern already used for `this.form` above.
    this.productFormComponent =
      this.closest('product-form-component') || this.section.querySelector('product-form-component');
    if (this.productFormComponent) {
      this.originalHandleSubmit = this.productFormComponent.handleSubmit.bind(this.productFormComponent);
      this.productFormComponent.handleSubmit = this.#wrappedHandleSubmit;
    }

    // Horizon replaces the price container on every variant change — re-snapshot
    // the new base price and re-apply the kit total after Horizon's handler runs.
    this.section.addEventListener('variant:update', this.#onVariantUpdate);

    // Opt-in pre-selection (theme setting `preselect_addons`). Route each card
    // through the same path a user click takes: flip the native checkbox, then
    // #syncCardFromCheckbox auto-picks the size for sized add-ons (main product's
    // size if it matches, else first available) and cleanly reverts any card with
    // nothing in stock. One #applyKitTotal at the end reflects the total once.
    if (this.dataset.preselect === 'true') {
      for (const card of this.cards) {
        const checkbox = card.querySelector('[data-pond-addon-checkbox]');
        if (!checkbox) continue;
        checkbox.checked = true;
        this.#syncCardFromCheckbox(card);
      }
      this.#applyKitTotal();
    }
  }

  disconnectedCallback() {
    if (this.productFormComponent && this.originalHandleSubmit) {
      // Restore so a re-rendered <pond-product-addons> doesn't double-wrap, and
      // so the host product-form survives if we're removed without it.
      this.productFormComponent.handleSubmit = this.originalHandleSubmit;
      this.productFormComponent = null;
      this.originalHandleSubmit = null;
    }
    if (this.section) {
      this.section.removeEventListener('variant:update', this.#onVariantUpdate);
    }
    // Size-list listeners (keydown/toggle/option) live on elements owned by
    // this component and are discarded with it; open/close/dismiss is native.
  }

  #onVariantUpdate = () => {
    // Defer one microtask so Horizon's product-price handler swaps the
    // priceContainer first; then we re-snapshot the new base and re-apply.
    queueMicrotask(() => {
      this.basePriceText = null;
      // Drop the secondary snapshots too — the new variant has new base
      // strings, and a node updated in place (rather than replaced) would
      // otherwise keep restoring the previous variant's price.
      for (const secondary of this.#getSecondaryPriceEls()) {
        delete secondary.dataset.pondBasePrice;
      }
      this.#applyKitTotal();
    });
  };

  /** Public API: which add-on variants are currently selected. */
  getSelectedItems() {
    const items = [];
    for (const card of this.cards) {
      if (!this.#isCardSelected(card)) continue;
      const variantId = this.#getCardVariantId(card);
      if (variantId) items.push({ variantId, quantity: 1 });
    }
    return items;
  }

  // ---- internals ----

  #isCardSelected(card) {
    return card.querySelector('[data-pond-addon-checkbox]')?.checked === true;
  }

  /**
   * Single source of truth for selection state. Always sync checkbox.checked,
   * card.dataset.selected, the size hidden input, the trigger display, and
   * the listbox aria states together. Every caller MUST go through this
   * helper — never poke at any of those directly elsewhere.
   */
  #setCardSelected(card, selected) {
    const checkbox = card.querySelector('[data-pond-addon-checkbox]');
    if (checkbox) checkbox.checked = selected;
    card.dataset.selected = selected ? 'true' : 'false';
    if (!selected) {
      const sizeInput = card.querySelector('[data-pond-addon-size]');
      if (sizeInput) {
        sizeInput.value = '';
        delete sizeInput.dataset.price;
      }
      const trigger = card.querySelector('[data-pond-addon-size-trigger]');
      const current = card.querySelector('[data-pond-addon-size-current]');
      if (trigger && current) current.textContent = trigger.dataset.placeholder || '';
      for (const opt of card.querySelectorAll('[data-pond-addon-size-option]')) {
        opt.setAttribute('aria-selected', 'false');
      }
      this.#closeSizeList(card);
    }
  }

  /**
   * Side effects when the native checkbox toggles. The browser has already
   * flipped checkbox.checked; we mirror it to dataset, and on first-select
   * for a sized add-on we auto-pick a variant (main product's size if it
   * exists in the addon's options, else first available).
   */
  #syncCardFromCheckbox(card) {
    const checkbox = card.querySelector('[data-pond-addon-checkbox]');
    if (!checkbox) return;

    if (!checkbox.checked) {
      this.#setCardSelected(card, false);
      return;
    }

    const sizeInput = card.querySelector('[data-pond-addon-size]');
    if (!sizeInput) {
      // one-size addon — just mirror the state
      this.#setCardSelected(card, true);
      return;
    }

    if (sizeInput.value) {
      card.dataset.selected = 'true';
      this.#updateCardPrice(card);
      return;
    }

    const mainSize = this.#getMainProductSize();
    const options = Array.from(card.querySelectorAll('[data-pond-addon-size-option]'));
    const enabled = options.filter((opt) => opt.getAttribute('aria-disabled') !== 'true');
    const matchingMain = mainSize
      ? enabled.find((opt) => (opt.dataset.size || '').toLowerCase() === mainSize.toLowerCase())
      : null;
    const pickedOption = matchingMain || enabled[0] || null;

    if (pickedOption) {
      this.#selectSizeOption(card, pickedOption, { focusTrigger: false });
      return;
    }

    // Nothing available → revert the selection cleanly.
    this.#setCardSelected(card, false);
  }

  /**
   * Read the main product's currently selected size (if any). Horizon's
   * variant picker emits one checked radio per option; we look for the radio
   * whose value matches one of the addon's size labels.
   */
  #getMainProductSize() {
    const checkedRadios = this.form.querySelectorAll('input[type="radio"]:checked');
    for (const radio of checkedRadios) {
      if (radio.value) return radio.value;
    }
    return null;
  }

  /** Update the card's displayed price to reflect the currently chosen variant. */
  #updateCardPrice(card) {
    const priceEl = card.querySelector('[data-pond-addon-price]');
    if (!priceEl) return;
    const sizeInput = card.querySelector('[data-pond-addon-size]');
    const cents = Number(
      sizeInput?.dataset.price || priceEl.dataset.defaultPrice || 0
    );
    if (!cents) return;
    // #rateFor is 1 once the real market price has loaded (cents already in the
    // active currency), else the base-currency × FX-rate fallback.
    const activeCents = Math.round(cents * this.#rateFor(card));
    priceEl.textContent = this.#formatMoney(activeCents);
  }

  /**
   * Fetch each add-on's real per-market price from /products/{handle}.js and
   * overwrite the base-currency data-* attributes with the active market's
   * fixed price-list amount. Same-origin fetch inherits the storefront's
   * market cookie, so the endpoint returns the localized fixed price (e.g.
   * €30.00 on the Eurozone market) rather than the DKK base or an FX-converted
   * approximation. On failure we leave the base-currency values + rate fallback
   * in place so the price is approximate but never blank.
   */
  #loadMarketPrices() {
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const inflight = new Map(); // handle → Promise, dedupe shared handles
    for (const card of this.cards) {
      const handle = card.dataset.addonHandle;
      if (!handle) continue;
      const url = `${root}products/${handle}.js`.replace(/\/{2,}/g, '/');
      let req = inflight.get(handle);
      if (!req) {
        req = fetch(url, { headers: { Accept: 'application/json' } })
          .then((r) => (r.ok ? r.json() : null));
        inflight.set(handle, req);
      }
      req
        .then((data) => {
          if (data && Array.isArray(data.variants)) this.#applyMarketPrices(card, data.variants);
        })
        .catch(() => { /* keep the base-currency fallback */ });
    }
  }

  /**
   * Overwrite a card's stored prices with the market variant prices (integer
   * minor units, already in the active currency), mark it loaded so #rateFor
   * stops applying the FX rate, then re-render the card price and kit total.
   */
  #applyMarketPrices(card, variants) {
    const byId = new Map(variants.map((v) => [String(v.id), Number(v.price)]));

    // Sized add-ons: refresh each option's data-price (+ the selected one).
    for (const opt of card.querySelectorAll('[data-pond-addon-size-option]')) {
      const cents = byId.get(String(opt.dataset.value));
      if (cents != null) opt.dataset.price = String(cents);
    }
    const sizeInput = card.querySelector('[data-pond-addon-size]');
    if (sizeInput && sizeInput.value) {
      const cents = byId.get(String(sizeInput.value));
      if (cents != null) sizeInput.dataset.price = String(cents);
    }

    // Card default (one-size add-ons + pre-size display): the default variant's
    // market price, else the first variant's.
    const priceEl = card.querySelector('[data-pond-addon-price]');
    const hidden = card.querySelector('[data-pond-addon-variant]');
    let defaultCents = hidden?.value ? byId.get(String(hidden.value)) : undefined;
    if (defaultCents == null && variants.length) defaultCents = Number(variants[0].price);
    if (priceEl && defaultCents != null) priceEl.dataset.defaultPrice = String(defaultCents);

    card.dataset.marketLoaded = 'true';
    this.#updateCardPrice(card);
    this.#applyKitTotal();
  }

  // ---- Custom size dropdown: open/close, option select, keyboard nav ----

  #closeSizeList(card) {
    const list = card.querySelector('[data-pond-addon-size-list]');
    // aria-expanded is mirrored by #onSizeListToggle when the popover closes.
    if (list && typeof list.hidePopover === 'function' && list.matches(':popover-open')) {
      list.hidePopover();
    }
  }

  /**
   * Mirror the native popover open/close onto the trigger's aria-expanded, and
   * move focus into the listbox on open so keyboard users land on the selected
   * (or first) option. Open/close/dismiss is otherwise fully native: the
   * trigger's `popovertarget` toggles, and `popover="auto"` handles outside-
   * click + Escape (and closes any other open size list). Positioning + close-
   * on-resize come from the wrapping <anchored-popover-component>.
   */
  #onSizeListToggle(card, e) {
    const trigger = card.querySelector('[data-pond-addon-size-trigger]');
    const open = e.newState === 'open';
    if (trigger) trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
    if (!open) return;
    const list = card.querySelector('[data-pond-addon-size-list]');
    const selected = list?.querySelector('[aria-selected="true"]');
    const first = list?.querySelector('[data-pond-addon-size-option]:not([aria-disabled="true"])');
    (selected || first)?.focus();
  }

  /**
   * Commit a size choice. Updates aria-selected, hidden input value+price,
   * trigger label text, ensures the card is checked, closes the list.
   * Pass `focusTrigger: false` for programmatic auto-picks (from
   * #syncCardFromCheckbox) so we don't steal focus from the card label.
   */
  #selectSizeOption(card, opt, { focusTrigger = true } = {}) {
    const trigger = card.querySelector('[data-pond-addon-size-trigger]');
    const current = card.querySelector('[data-pond-addon-size-current]');
    const sizeInput = card.querySelector('[data-pond-addon-size]');
    const checkbox = card.querySelector('[data-pond-addon-checkbox]');

    for (const sibling of card.querySelectorAll('[data-pond-addon-size-option]')) {
      sibling.setAttribute('aria-selected', sibling === opt ? 'true' : 'false');
    }
    if (sizeInput) {
      sizeInput.value = opt.dataset.value || '';
      sizeInput.dataset.price = opt.dataset.price || '';
    }
    if (current) current.textContent = opt.dataset.size || opt.textContent.trim();

    if (checkbox && !checkbox.checked) checkbox.checked = true;
    card.dataset.selected = 'true';

    // Close the popover (no-op for programmatic auto-picks where it isn't open);
    // #onSizeListToggle clears aria-expanded.
    this.#closeSizeList(card);
    if (focusTrigger && trigger) trigger.focus();

    this.#updateCardPrice(card);
  }

  #onSizeTriggerKeydown(card, e) {
    // Enter/Space toggle the popover natively via the button's `popovertarget`,
    // and Escape is handled by the auto popover — only add ArrowDown/Up to open.
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    const list = card.querySelector('[data-pond-addon-size-list]');
    if (!list || list.matches(':popover-open')) return;
    e.preventDefault();
    list.showPopover();
  }

  #onSizeOptionKeydown(card, opt, e) {
    const list = card.querySelector('[data-pond-addon-size-list]');
    if (!list) return;
    const options = Array.from(
      list.querySelectorAll('[data-pond-addon-size-option]:not([aria-disabled="true"])')
    );
    const idx = options.indexOf(opt);

    switch (e.key) {
      case 'Enter':
      case ' ':
        e.preventDefault();
        this.#selectSizeOption(card, opt);
        this.#applyKitTotal();
        break;
      case 'ArrowDown':
        e.preventDefault();
        (options[idx + 1] || options[0])?.focus();
        break;
      case 'ArrowUp':
        e.preventDefault();
        (options[idx - 1] || options[options.length - 1])?.focus();
        break;
      case 'Home':
        e.preventDefault();
        options[0]?.focus();
        break;
      case 'End':
        e.preventDefault();
        options[options.length - 1]?.focus();
        break;
      case 'Escape':
        e.preventDefault();
        this.#closeSizeList(card);
        card.querySelector('[data-pond-addon-size-trigger]')?.focus();
        break;
      case 'Tab':
        this.#closeSizeList(card);
        break;
      default:
        break;
    }
  }

  #getCardVariantId(card) {
    const sizeSelect = card.querySelector('[data-pond-addon-size]');
    if (sizeSelect) {
      return sizeSelect.value ? Number(sizeSelect.value) : null;
    }
    const hidden = card.querySelector('[data-pond-addon-variant]');
    return hidden?.value ? Number(hidden.value) : null;
  }

  #getMainVariantId() {
    // Mirror Horizon's #getIntendedVariantId (product-form.js:303): URL search
    // param first, then form input. variant-picker.js:111 updates ?variant=X via
    // history.replaceState on the next task after swatch click (yieldToMainThread
    // → scheduler.yield/rAF/setTimeout, ~0–16ms), while input[name="id"] only
    // updates after the variant-fragment fetch lands in #onVariantUpdate
    // (~50–200ms). Any ATC click occurs in a later task than the swatch click,
    // so the URL is always fresh by then — input is not. Reading URL-first
    // closes the realistic race window where the input still holds the previous
    // variant id between variant:selected and variant:update.
    const urlVariant = new URL(window.location.href).searchParams.get('variant');
    if (urlVariant) return Number(urlVariant);
    const input = this.form.querySelector('input[name="id"]');
    return input?.value ? Number(input.value) : null;
  }

  #getCartSectionIds() {
    const ids = [];
    for (const el of document.querySelectorAll('cart-items-component')) {
      const id = el instanceof HTMLElement ? el.dataset.sectionId : null;
      if (id) ids.push(id);
    }
    return ids.join(',');
  }

  // Wrapper installed onto <product-form-component>.handleSubmit. Falls through
  // to Horizon's original whenever we shouldn't combine: no add-ons selected,
  // variant resolution mid-flight (button disabled), or main variant missing.
  // Otherwise sends ONE multi-item POST per Shopify's nested-cart-lines docs.
  #wrappedHandleSubmit = (event) => {
    const addons = this.getSelectedItems();
    if (addons.length === 0) return this.originalHandleSubmit(event);

    // Mirror Horizon's #variantChangeInProgress guard — it queues the add for
    // after the variant resolves. The private field isn't readable, but the
    // ATC button's disabled state is the public proxy Horizon sets during it.
    // Match Horizon's own check (product-form.js:325) — .some() across all ATCs.
    const atcContainers = this.productFormComponent?.querySelectorAll('add-to-cart-component') ?? [];
    const anyDisabled = Array.from(atcContainers).some((c) => c.refs?.addToCartButton?.disabled);
    if (anyDisabled) return this.originalHandleSubmit(event);

    const mainVariantId = this.#getMainVariantId();
    if (!mainVariantId) return this.originalHandleSubmit(event);

    event.preventDefault();

    const items = [
      { id: mainVariantId, quantity: 1 },
      ...addons.map((a) => ({
        id: a.variantId,
        quantity: a.quantity,
        parent_id: mainVariantId,
      })),
    ];

    const sections = this.#getCartSectionIds();
    const payload = sections ? { items, sections } : { items };

    const cartAddUrl = (window.Theme && window.Theme.routes && window.Theme.routes.cart_add_url) || '/cart/add.js';

    fetch(cartAddUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload),
    })
      .then((res) => res.json())
      .then((response) => {
        if (response.status || response.errors) {
          // Use Horizon's CartErrorEvent so existing listeners (gift-card form,
          // toast notifications, etc.) read detail.data.{message,errors,description}
          // the shape they expect.
          document.dispatchEvent(
            new CartErrorEvent(
              'pond-product-addons',
              response.message,
              response.description,
              response.errors
            )
          );
          return;
        }
        // CartAddEvent fires under ThemeEvents.cartUpdate ('cart:update') so the
        // cart drawer, cart icon, and section morphers refresh exactly as they
        // would for any Horizon-native add-to-cart.
        document.dispatchEvent(
          new CartAddEvent(undefined, 'pond-product-addons', {
            source: 'pond-product-addons',
            productId: this.dataset.productId,
            itemCount: items.reduce((sum, i) => sum + (i.quantity || 1), 0),
            sections: response.sections,
          })
        );
        // Reset selections so the next add-to-cart starts fresh
        this.#resetSelections();
      })
      .catch((err) => {
        console.error('[pond-product-addons] cart/add.js failed:', err);
      });
  };

  #resetSelections() {
    for (const card of this.cards) {
      this.#setCardSelected(card, false);
    }
    this.#applyKitTotal();
  }

  // ---- ATC kit-total: rewrites Horizon's price element to reflect main + add-ons ----

  /**
   * Find the *visible* price text node inside Horizon's price block.
   *
   * snippets/price.liquid always renders BOTH the regular price
   * (`.price__regular > .price`) and the sale price
   * (`.price__sale > .price-item--sale.price`) and hides whichever doesn't
   * apply with `.price__hidden`. The regular block comes first in the DOM, so
   * a plain `querySelector('.price-item--sale.price, .price')` returns the
   * hidden regular node on a discounted product — the kit total would be
   * written somewhere invisible while the shopper's price never changes.
   * Pick the node inside the wrapper that is NOT hidden.
   */
  #getPriceTextEl() {
    const container = this.section?.querySelector?.('[ref="priceContainer"]');
    if (!container) return null;
    return (
      container.querySelector('.price__sale:not(.price__hidden) .price-item--sale.price') ||
      container.querySelector('.price__regular:not(.price__hidden) .price') ||
      container.querySelector('.price-item--sale.price, .price')
    );
  }

  #snapshotBaseIfNeeded() {
    if (this.basePriceText) return;
    const el = this.#getPriceTextEl();
    if (el) this.basePriceText = el.textContent;
  }

  /**
   * The other price nodes that must move with the kit total, or the buy box
   * contradicts itself:
   *  - the compare-at ("Regular price") strike-through — left alone it would
   *    read €99 next to a €107 total, i.e. a "was" price BELOW the price;
   *  - the PAS floating/sticky buy bar price, a second visible price rendered
   *    straight from Liquid in sections/main-product-pas.liquid.
   * Each keeps its own base snapshot in a dataset attr so deselecting restores
   * the original string byte-for-byte (and a re-render just re-snapshots).
   */
  #getSecondaryPriceEls() {
    const els = [];
    const container = this.section?.querySelector?.('[ref="priceContainer"]');
    const compareAt = container?.querySelector(
      '.price__sale:not(.price__hidden) .compare-at-price, .price__regular:not(.price__hidden) .compare-at-price'
    );
    if (compareAt) els.push(compareAt);
    for (const el of this.section?.querySelectorAll?.('.product-pas__floating-cart-price') ?? []) {
      els.push(el);
    }
    return els;
  }

  /**
   * Sum selected add-on prices in the **active market's** minor units.
   * Once #loadMarketPrices has run, data-price / data-default-price already
   * hold the market's fixed price-list amount (#rateFor → 1). Before that, or
   * if the fetch failed, they hold the base-currency metafield mirror and
   * #rateFor multiplies by Shopify.currency.rate.
   */
  #sumSelectedAddonCents() {
    let total = 0;
    for (const card of this.cards) {
      if (!this.#isCardSelected(card)) continue;
      // Sized addons store the chosen variant's price in data-price on the
      // hidden input (set by #selectSizeOption). One-size addons fall back
      // to the price element's data-default-price.
      const sizeInput = card.querySelector('[data-pond-addon-size]');
      const priceEl = card.querySelector('[data-pond-addon-price]');
      const cents = sizeInput
        ? Number(sizeInput.dataset.price || 0)
        : Number(priceEl?.dataset.defaultPrice || 0);
      total += Math.round(cents * this.#rateFor(card));
    }
    return total;
  }

  /**
   * Parse a rendered money string like "599,00 kr" / "$1,299.99" / "300 kr"
   * into integer cents. Locale-tolerant: last `.` or `,` followed by 1-2 digits
   * is treated as the decimal separator; any others are thousand separators.
   */
  #parsePriceTextToCents(text) {
    const cleaned = String(text).replace(/[^\d.,]/g, '');
    if (!cleaned) return 0;
    const lastDot = cleaned.lastIndexOf('.');
    const lastComma = cleaned.lastIndexOf(',');
    const lastSepAt = Math.max(lastDot, lastComma);
    if (lastSepAt === -1) return Math.round(Number(cleaned) * 100);
    const afterSep = cleaned.slice(lastSepAt + 1);
    if (afterSep.length <= 2 && /^\d+$/.test(afterSep)) {
      const whole = cleaned.slice(0, lastSepAt).replace(/[.,]/g, '');
      const wholeCents = (Number(whole) || 0) * 100;
      const fracCents = Math.round(Number(`0.${afterSep.padEnd(2, '0')}`) * 100);
      return wholeCents + fracCents;
    }
    return Math.round(Number(cleaned.replace(/[.,]/g, '')) * 100);
  }

  #applyKitTotal() {
    this.#snapshotBaseIfNeeded();
    if (!this.basePriceText) return;

    const el = this.#getPriceTextEl();
    if (!el) return;

    const addonCents = this.#sumSelectedAddonCents();

    for (const secondary of this.#getSecondaryPriceEls()) {
      if (secondary.dataset.pondBasePrice == null) {
        secondary.dataset.pondBasePrice = secondary.textContent;
      }
      const base = secondary.dataset.pondBasePrice;
      secondary.textContent =
        addonCents === 0
          ? base
          : this.#formatMoney(this.#parsePriceTextToCents(base) + addonCents);
    }

    if (addonCents === 0) {
      el.textContent = this.basePriceText;
      return;
    }
    const baseCents = this.#parsePriceTextToCents(this.basePriceText);
    el.textContent = this.#formatMoney(baseCents + addonCents);
  }

  /**
   * Format minor units using Horizon's built-in money formatter — same output
   * as Liquid's `| money` filter on the page (Markets-aware via `shop.money_format`).
   */
  #formatMoney(activeCurrencyCents) {
    const currency = window.Shopify?.currency?.active || this.dataset.shopCurrency || 'USD';
    return formatMoney(activeCurrencyCents, this.moneyFormat, currency);
  }

  /** Shop default → active market conversion rate (1 when not on a converted market). */
  #getCurrencyRate() {
    return Number(window.Shopify?.currency?.rate || 1);
  }

  /**
   * FX multiplier for a card's stored price. Once /products/{handle}.js has
   * supplied the market's fixed price (card.dataset.marketLoaded), the stored
   * cents are already in the active currency → multiplier 1. Before that, or if
   * the fetch failed, fall back to converting the base-currency metafield mirror
   * by Shopify.currency.rate.
   */
  #rateFor(card) {
    return card?.dataset.marketLoaded === 'true' ? 1 : this.#getCurrencyRate();
  }
}

if (!customElements.get('pond-product-addons')) {
  customElements.define('pond-product-addons', PondProductAddons);
}
